import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';

import { css } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

import { RoleOptions } from '../utilities/types';

import { LoomOnboardingText } from './loom-onboarding-text';

import type { Role } from '../utilities/types';

// Loom onboarding uses Atlassian Sans at 14/20 for body / interactive text
// (see LoomOnboardingText size="sm"). Match it in the role select trigger,
// menu, and option rows.
const fontFamily = 'var(--atlassian-sans-font-family)';
const fontSize = '0.875rem';
const lineHeight = '1.25rem';

// Design-spec drop shadow for the menu popover from the Loom onboarding mock.
const LOOM_ONBOARDING_MENU_SHADOW =
	'rgba(30, 31, 33, 0.15) 0px 8px 12px 0px, rgba(30, 31, 33, 0.31) 0px 0px 1px 0px';

// Selected-row colors — ADS "selected" blue pair.
const LOOM_ONBOARDING_SELECTED_TEXT = '#0C66E4';
const LOOM_ONBOARDING_SELECTED_BG = '#E9F2FF';

const fieldStyles = css({
	position: 'relative',
	width: '100%',
});

const triggerStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: '8px',
	width: '100%',
	minHeight: '40px',
	paddingBlock: '7px',
	paddingInline: '11px',
	fontFamily,
	fontSize,
	lineHeight,
	color: wacToken('color.neutral.light1100'),
	backgroundColor: wacToken('color.neutral.light0'),
	borderStyle: 'solid',
	borderWidth: '2px',
	borderColor: wacToken('color.neutral.light300'),
	borderRadius: '6px',
	textAlign: 'left',
	cursor: 'pointer',
	// Hover: swap the neutral border to the focus blue.
	':hover': {
		borderColor: token('color.border.focused'),
	},
	// Any focus (mouse or keyboard) tints the border blue so the trigger
	// still reads as the active field after the user commits a selection
	// (which returns focus to the trigger). The blue border replaces the
	// default keyboard focus ring — no outer outline is drawn.
	':focus': {
		borderColor: token('color.border.focused'),
		outline: 'none',
	},
	':focus-visible': {
		borderColor: token('color.border.focused'),
		outline: 'none',
	},
});

// Applied additively while the menu is open (see `isOpen` state) so the
// trigger keeps a blue border for the duration of the menu being open —
const triggerOpenStyles = css({
	borderColor: token('color.border.focused'),
});

const chevronStyles = css({
	display: 'inline-flex',
	flexShrink: 0,
	color: wacToken('color.neutral.light700'),
});

const menuStyles = css({
	position: 'absolute',
	insetInlineStart: 0,
	insetInlineEnd: 0,
	insetBlockStart: 'calc(100% + 12px)',
	zIndex: 20,
	display: 'flex',
	flexDirection: 'column',
	margin: 0,
	paddingBlock: '4px',
	paddingInline: 0,
	listStyle: 'none',
	outline: 'none',
	':focus': {
		outline: 'none',
	},
	':focus-visible': {
		outline: 'none',
	},
	fontFamily,
	fontSize,
	lineHeight,
	color: wacToken('color.neutral.light1100'),
	backgroundColor: wacToken('color.neutral.light0'),
	borderRadius: '6px',
	boxShadow: LOOM_ONBOARDING_MENU_SHADOW,
	maxHeight: '260px',
	overflowY: 'auto',
});

const optionStyles = css({
	paddingBlock: '8px',
	paddingInline: '12px',
	cursor: 'pointer',
	':hover': {
		backgroundColor: wacToken('color.neutral.light200'),
	},
});

// Keyboard-highlighted (active) row when it isn't the selected one.
const optionActiveStyles = css({
	backgroundColor: wacToken('color.neutral.light200'),
});

// Currently-selected row: blue text on a light-blue background. Wins over
// the neutral hover/active background because it's listed last in the
// `css={[...]}` array.
const optionSelectedStyles = css({
	color: LOOM_ONBOARDING_SELECTED_TEXT,
	backgroundColor: LOOM_ONBOARDING_SELECTED_BG,
	fontWeight: 'normal',
	':hover': {
		backgroundColor: LOOM_ONBOARDING_SELECTED_BG,
	},
});

export type LoomOnboardingRoleSelectProps = {
	/** DOM id for the trigger button, so an external `<label>` can point at it. */
	id: string;
	/** Space-separated id list for `aria-labelledby` on the trigger and listbox. */
	labelledBy: string;
	/** Currently selected role. */
	value: Role | undefined;
	/** Fired when the user commits a new role via click or keyboard. */
	onChange: (role: Role) => void;
};

/**
 * Custom-styled listbox for the Loom onboarding "What type of work do you
 * do?" question. Built from plain HTML (not @atlaskit/select) so the menu
 * shadow, radius, and blue selected-row styling from the mock can be
 * applied directly without fighting a third-party style pipeline.
 */
export const LoomOnboardingRoleSelect = ({
	id,
	labelledBy,
	value,
	onChange,
}: LoomOnboardingRoleSelectProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState(() =>
		Math.max(
			0,
			RoleOptions.findIndex((option) => option.value === value),
		),
	);
	const containerRef = useRef<HTMLDivElement | null>(null);
	const triggerRef = useRef<HTMLButtonElement | null>(null);
	const listRef = useRef<HTMLUListElement | null>(null);
	const listboxId = `${id}-listbox`;

	// Close on outside click.
	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const handlePointerDown = (event: MouseEvent) => {
			if (!containerRef.current?.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handlePointerDown);
		return () => document.removeEventListener('mousedown', handlePointerDown);
	}, [isOpen]);

	// When opening, sync the active row to the current value and scroll it in.
	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const currentIndex = Math.max(
			0,
			RoleOptions.findIndex((option) => option.value === value),
		);
		setActiveIndex(currentIndex);
	}, [isOpen, value]);

	// Keep the keyboard-highlighted row scrolled into view. Runs on open
	// (to reveal the currently-selected row) and every time the user
	// arrows / Home / End's to a different option so rows outside the
	// visible portion of the 260px-tall menu are brought into view.
	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const activeEl = listRef.current?.querySelector<HTMLLIElement>(
			`[data-role-option-index="${activeIndex}"]`,
		);
		activeEl?.scrollIntoView({ block: 'nearest' });
	}, [isOpen, activeIndex]);

	const commit = (index: number) => {
		const option = RoleOptions[index];
		if (option) {
			onChange(option.value);
		}
		setIsOpen(false);
		// Return focus to the trigger button so the user can immediately
		// re-open the menu, tab away, or otherwise continue the flow — and
		// so the visible focus indicator stays on the trigger rather than
		// jumping to document.body when the listbox unmounts.
		triggerRef.current?.focus();
	};

	const handleTriggerKeyDown = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
		if (
			event.key === 'ArrowDown' ||
			event.key === 'ArrowUp' ||
			event.key === 'Enter' ||
			event.key === ' '
		) {
			event.preventDefault();
			setIsOpen(true);
		}
	};

	const handleListKeyDown = (event: ReactKeyboardEvent<HTMLUListElement>) => {
		if (event.key === 'ArrowDown') {
			event.preventDefault();
			setActiveIndex((i) => Math.min(RoleOptions.length - 1, i + 1));
		} else if (event.key === 'ArrowUp') {
			event.preventDefault();
			setActiveIndex((i) => Math.max(0, i - 1));
		} else if (event.key === 'Home') {
			event.preventDefault();
			setActiveIndex(0);
		} else if (event.key === 'End') {
			event.preventDefault();
			setActiveIndex(RoleOptions.length - 1);
		} else if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			commit(activeIndex);
		} else if (event.key === 'Escape' || event.key === 'Tab') {
			setIsOpen(false);
		}
	};

	const activeOptionId = `${id}-option-${activeIndex}`;

	return (
		<div ref={containerRef} css={fieldStyles}>
			<button
				ref={triggerRef}
				id={id}
				type="button"
				css={[triggerStyles, isOpen && triggerOpenStyles]}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
				aria-controls={listboxId}
				aria-labelledby={`${labelledBy} ${id}`}
				onClick={() => setIsOpen((open) => !open)}
				onKeyDown={handleTriggerKeyDown}
				data-testid="loom-onboarding-role-select"
			>
				{value ? (
					<span>{value}</span>
				) : (
					<LoomOnboardingText size="sm" inline>
						Choose role
					</LoomOnboardingText>
				)}
				<span aria-hidden="true" css={chevronStyles}>
					<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
						<path
							d="M3 4.5L6 7.5L9 4.5"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</span>
			</button>

			{isOpen && (
				<ul
					id={listboxId}
					role="listbox"
					tabIndex={-1}
					aria-labelledby={labelledBy}
					aria-activedescendant={activeOptionId}
					css={menuStyles}
					onKeyDown={handleListKeyDown}
					ref={(node) => {
						listRef.current = node;
						node?.focus();
					}}
				>
					{RoleOptions.map((option, index) => {
						const isActive = index === activeIndex;
						const isSelected = option.value === value;
						return (
							<li
								key={option.value}
								id={`${id}-option-${index}`}
								role="option"
								aria-selected={isSelected}
								data-role-option-index={index}
								css={[
									optionStyles,
									isActive && !isSelected && optionActiveStyles,
									isSelected && optionSelectedStyles,
								]}
								onMouseEnter={() => setActiveIndex(index)}
								onFocus={() => setActiveIndex(index)}
								onMouseDown={(event) => {
									// Prevent the trigger from losing focus before commit.
									event.preventDefault();
								}}
								onClick={() => commit(index)}
								// Keyboard navigation is handled at the parent <ul> level
								// (arrow keys, Enter, Home/End). This no-op satisfies the
								// a11y rule that click handlers on non-standard interactive
								// elements have a keyboard counterpart.
								onKeyDown={() => {}}
							>
								{option.value}
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};
